/**
 * Shared SIP projection + end-date math.
 *
 * Used by both the mobile and web SIP setup screens to render:
 *  - the computed SIP end date ("Your SIP end date will be DD/MM/YYYY")
 *  - the "Accelerated Growth" With / Without Top-Up projection card.
 *
 * This is a PRESENTATIONAL ESTIMATE for the UI only — it is NOT the figure the
 * backend books. All projections are illustrative ("based on past performance;
 * future returns may vary"), so the numbers are intentionally approximate.
 */

// Illustrative assumed annual return used for the projection card. The Figma
// mock uses ~24.3% p.a. ("+24.30% p.a."); kept as a single named constant so a
// product/compliance change is one edit and both platforms stay in sync.
// NOTE: single hardcoded rate — if the backend ever returns a scheme-specific
// expected return, pass it in via opts.annualReturnRate instead.
export const SIP_PROJECTION_ANNUAL_RETURN = 0.243;

export type SipFrequency = "DAILY" | "WEEKLY" | "FORTNIGHTLY" | "MONTHLY" | "QUARTERLY";

/** Number of SIP installments in one year for a given frequency. */
export const installmentsPerYear = (frequency: string): number => {
  switch (String(frequency || "").toUpperCase()) {
    case "DAILY":
      return 250; // ~250 market working days
    case "WEEKLY":
      return 52;
    case "FORTNIGHTLY":
      return 26;
    case "QUARTERLY":
      return 4;
    case "MONTHLY":
    default:
      return 12;
  }
};

/**
 * SIP end date = start date + N years (same calendar day). Matches the Figma
 * "02/06/2029" for a 3Y SIP starting 02/06/2026. Returns a DD/MM/YYYY string,
 * or null for invalid input so callers can hide the note.
 *
 * Dependency-free (no dayjs import) so it stays trivially reusable on web and
 * mobile.
 */
export const computeSipEndDate = (
  startDateISO: string | null | undefined,
  years: number,
): string | null => {
  if (!startDateISO || !years || years <= 0) return null;
  const start = new Date(startDateISO);
  if (isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + years);
  // Format as DD/MM/YYYY.
  const dd = String(end.getDate()).padStart(2, "0");
  const mm = String(end.getMonth() + 1).padStart(2, "0");
  const yyyy = end.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Final (last-installment) SIP amount after all top-ups, used as the top-up
 * `maxLimit` in the checkout payload.
 *
 * The SIP installment steps up by `topUpAmount` at each top-up cycle (every 6
 * months for "6M"/"Half-Yearly", every 12 months for "1Y"/"Yearly") across the
 * SIP tenure (startDate -> endDate). The last installment therefore equals:
 *
 *   sipAmount + topUpAmount * (number of top-up cycles that occur over the tenure)
 *
 * The number of cycles is floor(tenureMonths / periodMonths): a step-up lands
 * at the end of each completed period, so a 3-year yearly top-up applies 3
 * step-ups. Returns the base sipAmount when there's no valid top-up / tenure.
 */
export const computeTopUpMaxLimit = (opts: {
  sipAmount: number;
  topUpAmount: number;
  /** UI value ("6M" | "1Y") or API cadence ("Half-Yearly" | "Yearly"). */
  topUpFrequency?: string;
  /**
   * Preferred: the SIP tenure in whole years. Pass this so the maxLimit uses
   * the SAME horizon the projection/business rule uses — notably 30 years for
   * Perpetual, where there is no concrete end date. When omitted, the tenure is
   * derived from startDate -> endDate instead.
   */
  tenureYears?: number;
  /** SIP start/end as anything dayjs/Date can parse, or ISO strings. */
  startDate?: string | Date | null;
  endDate?: string | Date | null;
}): number => {
  const sip = Number(opts.sipAmount) || 0;
  const step = Number(opts.topUpAmount) || 0;
  if (sip <= 0 || step <= 0) return sip;

  const freq = String(opts.topUpFrequency || "1Y").toUpperCase();
  const periodMonths = freq === "6M" || freq.startsWith("HALF") ? 6 : 12;

  // Prefer an explicit tenure (in years) — e.g. 30 for Perpetual — so the
  // maxLimit matches the projection horizon rather than the mandate-aligned
  // payload end date (which for Perpetual is only a near-term date).
  let tenureMonths: number;
  if (typeof opts.tenureYears === "number" && opts.tenureYears > 0) {
    tenureMonths = Math.round(opts.tenureYears * 12);
  } else {
    const start = opts.startDate ? new Date(opts.startDate) : null;
    const end = opts.endDate ? new Date(opts.endDate) : null;
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return sip;
    if (end.getTime() <= start.getTime()) return sip;
    tenureMonths =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      (end.getDate() >= start.getDate() ? 0 : -1);
  }
  if (tenureMonths <= 0) return sip;

  const cycles = Math.floor(tenureMonths / periodMonths);
  if (cycles <= 0) return sip;

  return sip + step * cycles;
};

/**
 * Resolve the SIP tenure in whole years from the chosen duration, mirroring the
 * top-up projection horizon so the payload's top-up maxLimit matches the UI:
 *   • "PERPETUAL"           -> 30 (no end date; 30-year horizon)
 *   • "3Y" / "5Y"           -> 3 / 5
 *   • "CUSTOM" + DURATION   -> the entered years
 *   • "CUSTOM" + END_DATE   -> whole years between start and end (min 1)
 *   • otherwise, if start/end exist -> whole years between them (min 1)
 * Returns 0 when nothing usable is available (caller then falls back to dates).
 */
export const resolveTopUpTenureYears = (
  sipDuration?: string,
  sipCustomMode?: string,
  sipDurationYears?: number | string,
  startDate?: string | Date | null,
  endDate?: string | Date | null,
): number => {
  // Fixed-year durations short-circuit to their exact tenure.
  if (sipDuration === "3Y") return 3;
  if (sipDuration === "5Y") return 5;
  if (sipDuration === "CUSTOM" && sipCustomMode === "DURATION") {
    const y = Number(sipDurationYears);
    if (y > 0) return y;
  }

  // Everything else (Perpetual, Custom + End Date, or any case with a concrete
  // end date) derives the tenure from start -> end in whole years (min 1).
  // For Perpetual the passed endDate IS the mandate's end date (mandate MaxDate,
  // aligned), so the top-up cycles are counted against the real mandate horizon
  // rather than an assumed 30 years.
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
    if (end.getTime() > start.getTime()) {
      let years = end.getFullYear() - start.getFullYear();
      const beforeAnniversary =
        end.getMonth() < start.getMonth() ||
        (end.getMonth() === start.getMonth() && end.getDate() < start.getDate());
      if (beforeAnniversary) years -= 1;
      return Math.max(1, years);
    }
  }

  // Only when no usable end date is available: fall back to the 30-year horizon
  // for Perpetual (matches the projection), else 0 (caller handles).
  if (sipDuration === "PERPETUAL") return 30;
  return 0;
};

export interface SipProjectionInput {
  /** Per-installment amount at the start of the SIP. */
  installmentAmount: number;
  /** SIP cadence; drives installments-per-year and the per-period rate. */
  frequency: string;
  /** Total SIP tenure in years. */
  years: number;
  /** Annual step-up percentage (e.g. 10 for +10%/year). 0 = no step-up. */
  stepUpPercent?: number;
  /** Override the assumed annual return (fraction, e.g. 0.12). */
  annualReturnRate?: number;
}

export interface SipProjectionResult {
  /** Total amount actually invested over the tenure (sum of installments). */
  invested: number;
  /** Projected maturity value (future value) at the assumed return. */
  futureValue: number;
}

/**
 * Future value of a SIP, with optional annual step-up.
 *
 * Model: installments compound at a per-period rate derived from the assumed
 * annual return. When stepUpPercent > 0, the installment increases by that
 * percentage at the start of each subsequent year (standard "top-up SIP").
 *
 * This is an O(totalInstallments) accumulation — exact for the model, no
 * closed-form rounding drift. For DAILY over many years that's a few thousand
 * iterations, which is fine for a one-off UI calc.
 */
export const projectSip = (input: SipProjectionInput): SipProjectionResult => {
  const {
    installmentAmount,
    frequency,
    years,
    stepUpPercent = 0,
    annualReturnRate = SIP_PROJECTION_ANNUAL_RETURN,
  } = input;

  const perYear = installmentsPerYear(frequency);
  const totalInstallments = Math.max(0, Math.round(perYear * years));
  if (!installmentAmount || installmentAmount <= 0 || totalInstallments === 0) {
    return { invested: 0, futureValue: 0 };
  }

  // Per-period growth rate: the nominal annual rate divided by the number of
  // installments per year. This matches standard SIP calculators (AMFI, Groww,
  // etc.) which use a simple monthly rate of annualReturn/12 — NOT the geometric
  // (1+annual)^(1/perYear)-1, which under-states the maturity value.
  const periodRate = annualReturnRate / perYear;
  const stepUp = Math.max(0, stepUpPercent) / 100;

  let invested = 0;
  let futureValue = 0;
  let currentInstallment = installmentAmount;

  for (let i = 0; i < totalInstallments; i++) {
    // Step up the installment at each year boundary (after the first year).
    if (i > 0 && i % perYear === 0 && stepUp > 0) {
      currentInstallment = currentInstallment * (1 + stepUp);
    }
    invested += currentInstallment;
    // Each installment compounds for the remaining periods.
    const periodsRemaining = totalInstallments - i;
    futureValue += currentInstallment * Math.pow(1 + periodRate, periodsRemaining);
  }

  return {
    invested: Math.round(invested),
    futureValue: Math.round(futureValue),
  };
};

export interface TopUpComparison {
  withTopUp: SipProjectionResult;
  withoutTopUp: SipProjectionResult;
  /** Extra maturity value from the top-up (withTopUp.futureValue - withoutTopUp.futureValue). */
  extraValue: number;
  /** Extra value as a % of the plain-SIP future value, rounded. */
  extraPercent: number;
}

/**
 * Compares a plain SIP against the same SIP with an annual step-up, for the
 * "Accelerated Growth" card. withoutTopUp always passes stepUpPercent = 0.
 */
export const compareTopUpProjection = (
  input: Omit<SipProjectionInput, "stepUpPercent"> & { stepUpPercent: number },
): TopUpComparison => {
  const withTopUp = projectSip(input);
  const withoutTopUp = projectSip({ ...input, stepUpPercent: 0 });
  const extraValue = Math.max(0, withTopUp.futureValue - withoutTopUp.futureValue);
  const extraPercent =
    withoutTopUp.futureValue > 0
      ? Math.round((extraValue / withoutTopUp.futureValue) * 100)
      : 0;
  return { withTopUp, withoutTopUp, extraValue, extraPercent };
};

/** Formats a number as Indian-grouped rupees, e.g. 225618 -> "₹2,25,618". */
export const formatIndianRupee = (value: number): string =>
  `₹${Math.round(value || 0).toLocaleString("en-IN")}`;

/**
 * Message shown when the chosen SIP duration yields fewer than the scheme's
 * minimum installments for the entered amount, e.g.:
 *   "Minimum SIP Amount for Monthly SIP should ₹100 for 12 Months."
 * The period word tracks the frequency (Days / Weeks / Fortnights / Months /
 * Quarters). minAmount falls back to ₹100 when the cycle config omits it.
 */
export const buildMinSipMessage = (
  frequency: string,
  minAmount: number | undefined,
  minInstallments: number,
): string => {
  const f = String(frequency || "MONTHLY").toUpperCase();
  const freqLabel =
    f.charAt(0) + f.slice(1).toLowerCase(); // MONTHLY -> Monthly
  const periodWord =
    f === "DAILY"
      ? "Days"
      : f === "WEEKLY"
        ? "Weeks"
        : f === "FORTNIGHTLY"
          ? "Fortnights"
          : f === "QUARTERLY"
            ? "Quarters"
            : "Months";
  const amt = minAmount && minAmount > 0 ? minAmount : 100;
  return `Minimum SIP Amount for ${freqLabel} SIP should ₹${amt} for ${minInstallments} ${periodWord}.`;
};
